import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Application } from '../models/application';
import { JsonFormData } from '../models/jobJsonForm';
import { EnvironmentService } from './environment.service';
import { CustomAction } from '../models/customAction';
import { EventEmitter } from 'stream';

@Injectable({
  providedIn: "root",
})
export class ApplicationService {
  // for storing clicked application name
  private AppName = new BehaviorSubject<string>("");
  public appName = this.AppName.asObservable();  
  private customActionAdded = new Subject<void>();
  customActionAdded$: Observable<void> = this.customActionAdded;

  sendAppName(name: string) {
    this.AppName.next(name);
  }

  // for storing clicked application version
  private AppVer = new BehaviorSubject<string>("");
  public appVer = this.AppVer.asObservable();

  sendAppVer(name: string) {
    this.AppVer.next(name);
  }

  private selectedApplication = new BehaviorSubject<Application | null>(null);
  public SelectedApplication = this.selectedApplication.asObservable();
  updateSelectedApplication(app: Application) {
    if (this.selectedApplication.value != app) {
      this.selectedApplication.next(app);
    }
  }

  private addedUniqueNames = new BehaviorSubject<string[]>([]);
  public AddedUniqueNames = this.addedUniqueNames.asObservable();

  updateAddedUniqueNames(name: string) {
    let arr = this.addedUniqueNames.value;
    if (arr.includes(name.toLowerCase())) return;
    arr.push(name.toLowerCase());
    this.addedUniqueNames.next(arr);
  }

  removeUniqueName(name: string) {
    let idx = this.addedUniqueNames.value.findIndex(
      (uniqueName) => uniqueName == name.toLowerCase()
    );
    if (idx != -1) this.addedUniqueNames.value.splice(idx, 1);
  }

  resetUniqueNames() {
    this.addedUniqueNames.next([]);
  }

  tempDeployName; //temporary variable for storing deployment name used in app-summary comp

  constructor(private http: HttpClient, private env: EnvironmentService) {}

  getAllApplicationsForUser(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ApplicationInfoes/Deployment/" +
      depName +
      "/Applications";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("checkUserDepAccess", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getAllApplicationNamesForUser(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ApplicationInfoes/Deployment/" +
      depName +
      "/GetApplicationNames";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("checkUserDepAccess", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getApplicationNameVersForUser(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `ApplicationInfoes/Sessions/ApplicationNames/${depName}`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("checkUserDepAccess", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getApplicationDetails(depName: string, appName: string, appVer: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ApplicationInfoes/Deployment/" +
      depName +
      "/Applications/" +
      appName +
      "/" +
      appVer;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("checkUserDepAccess", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getApplicationVersions(depName: string, appName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ApplicationInfoes/Deployment/" +
      depName +
      "/Applications/" +
      appName +
      "/versions";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("checkUserDepAccess", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getNodeGroups(depName: string, appName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ApplicationInfoes/Deployment/" +
      depName +
      "/Applications/" +
      appName +
      "/nodegroups";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("checkUserDepAccess", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  getNodeGroupsForDeployment(depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ApplicationInfoes/Deployment/" +
      depName +
      "/nodegroups";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  addApplication(newAppData: Application, depName, sourceAppId?: string) {
    try {
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "ApplicationInfoes/Deployment/" +
        depName +
        "/ApplicationInfoes/AddApplication";
      let headers = new HttpHeaders();
      const appInfo = JSON.stringify(newAppData);
      headers = headers
        .set("Content-Type", "application/json; charset=utf-8")
        .set("Cache-Control", "no-cache");
      // Add source app ID to headers if this is a clone operation
      if (sourceAppId) {
        headers = headers.set('SourceAppId', sourceAppId);
      }
      const upload$ = this.http.post<any>(baseUrl, appInfo, {
        headers: headers,
        reportProgress: true,
        observe: "events",
      });
      return upload$;
    } catch (Error) {}
  }

  deleteApplication(delAppData: Application, depName) {
    try {
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "ApplicationInfoes/Deployment/" +
        depName +
        "/ApplicationInfoes/RemoveApplication";
      let headers = new HttpHeaders();
      const appInfo = JSON.stringify(delAppData);
      headers = headers
        .set("Content-Type", "application/json; charset=utf-8")
        .set("Cache-Control", "no-cache");
      const upload$ = this.http.put<any>(baseUrl, appInfo, {
        headers: headers,
      });
      return upload$;
    } catch (Error) {}
  }

  updateApplication(depName, updateAppData: Application) {
    try {
      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "ApplicationInfoes/Deployment/" +
        depName +
        "/ApplicationInfoes/UpdateApplication";
      let headers = new HttpHeaders();
      const appInfo = JSON.stringify(updateAppData);
      headers = headers
        .set("Content-Type", "application/json; charset=utf-8")
        .set("Cache-Control", "no-cache");
      const upload$ = this.http.put<any>(baseUrl, appInfo, {
        headers: headers,
      });
      return upload$;
    } catch (Error) {}
  }

  UploadApplicationFile(
    depName: string,
    appName: string,
    appVer: string,
    fileType: string,
    myFile: File
  ) {
    try {
      const formData = new FormData();
      formData.append("File", myFile);

      let baseUrl =
        this.env.envVariables.httpProtocol +
        this.env.envVariables.webHost +
        "ApplicationInfoes/Deployment/" +
        depName +
        "/Applications/" +
        appName +
        "/" +
        appVer +
        "/Upload";
      let headers = new HttpHeaders();
      headers = headers
        .set("Cache-Control", "no-cache")
        .set("Access-Control-Allow-Origin", "*")
        .set(
          "Access-Control-Allow-Headers",
          "Origin, X-Requested-With, Content-Type, Accept"
        )
        .set("FileType", fileType);

      const upload$ = this.http
        .post<any>(baseUrl, formData, {
          headers: headers,
          reportProgress: true,
          observe: "events",
        })
        .pipe(finalize(() => {}));

      return upload$;
    } catch (Error) {}
  }

  getAllApplications(userId) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ApplicationInfoes/SupportedApplications";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  updateJobJson(
    depName: string,
    appName: string,
    appVer: string,
    jsonObj: JsonFormData
  ) {
    let baseUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}ApplicationInfoes/Deployment/${depName}/Application/${appName}/${appVer}/AddJsonForm`;

    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post(baseUrl, jsonObj, { headers: headers });
  }

  getJobJson(depName: string, appName: string, appVer: string) {
    let baseUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}ApplicationInfoes/Deployment/${depName}/Application/${appName}/${appVer}/GetJsonForm`;

    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("checkUserDepAccess", depName);
    return this.http.get(baseUrl, { headers: headers });
  }

  GetApplicationFileContent(
    depName: string,
    appName: string,
    appVer: string,
    fileType: string,
    myFile: string
  ) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "ApplicationInfoes/Deployment/" +
      depName +
      "/Applications/" +
      appName +
      "/" +
      appVer +
      "/GetContent";
    let headers = new HttpHeaders();
    headers = headers
      .set("Cache-Control", "no-cache")
      .set("Access-Control-Allow-Origin", "*")
      .set(
        "Access-Control-Allow-Headers",
        "Origin, X-Requested-With, Content-Type, Accept"
      )
      .set("FileType", fileType)
      .set("FileName", myFile);

    return this.http.get(baseUrl, { headers: headers });
  }

  getApplicationStaticCores(
    depName: string,
    appName: string,
    appVer: string,
    currentLoggedInUser: string = null
  ) {
    let baseUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}ApplicationInfoes/Deployment/${depName}/Application/${appName}/${appVer}/GetStaticCores`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("CurrentLoggedInUser", currentLoggedInUser);
    return this.http.get(baseUrl, { headers: headers });
  }

  addCustomAction(depName:string, userId: string, appName: string, appVer: string, customAction: CustomAction ){
    let baseUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}ApplicationInfoes/Deployment/${depName}/Application/${appName}/${appVer}/AddCustomAction`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type','application/json; charset=utf-8')
      .set('Cache-Control','no-cache')
      .set("UserId",userId);
    return this.http.post(baseUrl, customAction, {headers: headers});
  }

  getCustomActionByAppId(depName: string,userId: string, appName: string, appVer: string, appId: string){
    let baseUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}ApplicationInfoes/Deployment/${depName}/Application/${appName}/${appVer}/GetCustomActions`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type','application/json; charset=utf-8')
      .set('Cache-Control','no-cache')
      .set("UserId",userId)
      .set("AppId",appId);
    return this.http.get(baseUrl, {headers: headers});
  }

  updateCustomAction(depName:string, userId: string, appName: string, appVer: string, customAction: CustomAction ){
    let baseUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}ApplicationInfoes/Deployment/${depName}/Application/${appName}/${appVer}/UpdateCustomAction`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type','application/json; charset=utf-8')
      .set('Cache-Control','no-cache')
      .set("UserId",userId);
    return this.http.post(baseUrl, customAction, {headers: headers});
  }

  deleteCustomAction(depName:string, userId: string, appName: string, appVer: string, appId:string, customActionId: string ){
    let baseUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}ApplicationInfoes/Deployment/${depName}/Application/${appName}/${appVer}/DeleteCustomAction`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type','application/json; charset=utf-8')
      .set('Cache-Control','no-cache')
      .set("UserId",userId)
      .set("AppId",appId);
    return this.http.post(baseUrl, JSON.stringify(customActionId), {headers: headers});
  }

  getCustomActionsForJobList(depName:string, userId: string, appIds: string){
    let baseUrl = `${this.env.envVariables.httpProtocol}${this.env.envVariables.webHost}ApplicationInfoes/Deployment/${depName}/User/${userId}/GetCustomActions`;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type','application/json; charset=utf-8')
    .set('Cache-Control','no-cache')
    .set("AppIds",appIds);
    return this.http.get(baseUrl, {headers: headers});
  }

  notifyCustomActionAdded() {
    this.customActionAdded.next();
  }
}
